import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Loader2, Dumbbell, ChevronLeft, Camera, Film, Clapperboard, CheckCircle2, Sparkles, RefreshCw, UploadCloud, ImageOff } from 'lucide-react';
import { toast } from 'sonner';
import { MuscleGroupChip, ExerciseImage } from '../tracking/workouts/ExerciseLibraryUI';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ACCEPTED_UPLOAD_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** Lit un fichier image local (validé JPG/PNG/WebP, 5 Mo max) et renvoie { base64, contentType,
 * previewUrl } prêt à envoyer au backend — utilisé pour l'upload "telle quelle" ET la référence IA. */
const readUploadFile = (file) => new Promise((resolve, reject) => {
  if (!ACCEPTED_UPLOAD_TYPES.includes(file.type)) { reject("Format non supporté (JPG, PNG ou WebP uniquement)."); return; }
  if (file.size > MAX_UPLOAD_BYTES) { reject('Image trop volumineuse (5 Mo maximum).'); return; }
  const reader = new FileReader();
  reader.onload = () => resolve({ base64: reader.result.split(',')[1], contentType: file.type, previewUrl: reader.result });
  reader.onerror = () => reject('Lecture du fichier échouée.');
  reader.readAsDataURL(file);
});

/** Devine le matériel le plus probable à partir du nom (évite de faire choisir l'admin à
 * chaque création — reste modifiable). */
const guessEquipment = (name) => {
  const n = (name || '').toLowerCase();
  if (/halt[eè]re/.test(n)) return 'halteres';
  if (/\bbarre\b/.test(n)) return 'barre_libre';
  if (/poulie|c[aâ]ble/.test(n)) return 'poulie';
  if (/machine/.test(n)) return 'machine_guidee';
  if (/[eé]lastique|bande|kettlebell|trx|lest|swiss ?ball|m[eé]decine/.test(n)) return 'petit_materiel';
  return 'poids_du_corps';
};

const STYLE_CHOICES = [
  { key: 'photoreal_static', label: 'Photo réaliste', Icon: Camera, testid: 'create-style-photo-btn' },
  { key: 'muscle_storyboard', label: 'Storyboard musculaire', Icon: Clapperboard, testid: 'create-style-storyboard-btn' },
];

/** Picker visuel de la Bibliothèque d'exercices V2 (images/vidéos, ~500 exercices) pour
 * construire/éditer un programme (neuf ou déjà assigné à un client — même flux) : recherche
 * live + filtre par groupe musculaire + création intégrée si l'exercice n'existe pas encore
 * (nom -> style d'image au choix -> vidéo Sora 2 optionnelle -> utilisation immédiate). */
export const ExercisePickerModal = ({ onClose, onSelect, initialQuery = '', csrfToken }) => {
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [levels, setLevels] = useState([]);
  const [activeGroup, setActiveGroup] = useState('');
  const [q, setQ] = useState(initialQuery);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: initialQuery, muscle_group: '', equipment: guessEquipment(initialQuery), level: 'intermediaire' });
  const [stage, setStage] = useState('form'); // form | generating | ready | error
  const [createErr, setCreateErr] = useState('');
  const [createFeedback, setCreateFeedback] = useState('');
  const [createdEx, setCreatedEx] = useState(null);
  const [videoStage, setVideoStage] = useState('idle'); // idle | generating | ready | failed
  const [showVideoBlock, setShowVideoBlock] = useState(true);
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenErr, setRegenErr] = useState('');
  const [regenFeedback, setRegenFeedback] = useState('');
  const [regenCandidate, setRegenCandidate] = useState(null); // { image_url, image_path, style }
  const [pendingRevertIdx, setPendingRevertIdx] = useState(null);
  const [refImage, setRefImage] = useState(null); // { base64, contentType, previewUrl } — création
  const [refImageErr, setRefImageErr] = useState('');
  const [uploadingAsIs, setUploadingAsIs] = useState(false);
  const [regenRefImage, setRegenRefImage] = useState(null); // upload de référence — régénération
  const [regenRefImageErr, setRegenRefImageErr] = useState('');

  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options, credentials: 'include', headers: { ...options.headers, 'X-CSRF-Token': csrfToken },
  });

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/exercises/meta`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => {
        setMuscleGroups(d.muscle_groups || []);
        setEquipments(d.equipments || []);
        setLevels(d.levels || []);
      }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.append('q', q.trim());
    if (activeGroup) params.append('muscle_group', activeGroup);
    fetch(`${BACKEND_URL}/api/exercises?${params}`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setItems(d.exercises || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q, activeGroup]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => {
    setForm({ name: q.trim(), muscle_group: '', equipment: guessEquipment(q), level: 'intermediaire' });
    setStage('form'); setCreateErr(''); setCreatedEx(null); setVideoStage('idle');
    setRegenCandidate(null); setRegenErr(''); setPendingRevertIdx(null); setCreateFeedback(''); setRegenFeedback('');
    setRefImage(null); setRefImageErr(''); setRegenRefImage(null); setRegenRefImageErr('');
    setCreateOpen(true);
    if (q.trim().length > 1) {
      fetch(`${BACKEND_URL}/api/admin/programs-pro/detect-muscle-group?name=${encodeURIComponent(q.trim())}`, { credentials: 'include' })
        .then((r) => r.json()).then((d) => { if (d.muscle_group) setForm((f) => ({ ...f, muscle_group: d.muscle_group })); })
        .catch(() => {});
    }
  };

  const onNameBlur = () => {
    if (form.name.trim().length < 2) return;
    setForm((f) => ({ ...f, equipment: guessEquipment(f.name) }));
    fetch(`${BACKEND_URL}/api/admin/programs-pro/detect-muscle-group?name=${encodeURIComponent(form.name.trim())}`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => { if (d.muscle_group) setForm((f) => ({ ...f, muscle_group: d.muscle_group })); })
      .catch(() => {});
  };

  const parseErrDetail = async (errOrResponse) => {
    if (errOrResponse && typeof errOrResponse.json === 'function') {
      try { return (await errOrResponse.json())?.detail || null; } catch { return null; }
    }
    return null;
  };

  const findExistingByName = async (name) => {
    const res = await fetch(`${BACKEND_URL}/api/exercises?q=${encodeURIComponent(name)}`, { credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    return (data.exercises || []).find((e) => (e.name_fr || '').trim().toLowerCase() === name.trim().toLowerCase());
  };

  const startCreate = async (style) => {
    if (!form.name.trim() || !form.muscle_group) { setCreateErr('Nom et groupe musculaire requis.'); return; }
    setStage('generating'); setCreateErr('');
    try {
      let exId;
      const createRes = await fetchWithCsrf(`${BACKEND_URL}/api/exercises`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name_fr: form.name.trim(), muscle_group: form.muscle_group, equipment: form.equipment, level: form.level }),
      });
      if (createRes.ok) {
        exId = (await createRes.json()).id;
      } else if (createRes.status === 409) {
        // Nom déjà pris — probablement une tentative précédente qui a échoué plus loin dans le
        // flux (texte/image). On reprend sur cet exercice existant au lieu d'échouer bêtement.
        const existing = await findExistingByName(form.name.trim());
        if (!existing) throw createRes;
        exId = existing.id;
      } else {
        throw createRes;
      }

      const currentDoc = await fetch(`${BACKEND_URL}/api/exercises/${exId}`, { credentials: 'include' }).then((r) => r.json());
      if (!currentDoc.execution || !currentDoc.execution.trim()) {
        const text = await fetchWithCsrf(`${BACKEND_URL}/api/exercises/${exId}/generate-execution-text`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
        }).then((r) => (r.ok ? r.json() : Promise.reject(r)));
        await fetchWithCsrf(`${BACKEND_URL}/api/exercises/${exId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ execution: text.execution, tips: text.tips }),
        });
      }

      const img = await fetchWithCsrf(
        refImage ? `${BACKEND_URL}/api/exercises/${exId}/generate-image-candidate-from-upload` : `${BACKEND_URL}/api/exercises/${exId}/generate-image-candidate`,
        {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(refImage
            ? { style, feedback: createFeedback.trim(), image_base64: refImage.base64, content_type: refImage.contentType }
            : { style, feedback: createFeedback.trim() }),
        },
      ).then((r) => (r.ok ? r.json() : Promise.reject(r)));
      await fetchWithCsrf(`${BACKEND_URL}/api/exercises/${exId}/confirm-image`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: img.image_url, image_path: img.image_path, style }),
      });

      const final = await fetch(`${BACKEND_URL}/api/exercises/${exId}`, { credentials: 'include' }).then((r) => r.json());
      setCreatedEx(final);
      setShowVideoBlock(true);
      setStage('ready');
    } catch (err) {
      const detail = await parseErrDetail(err);
      setCreateErr(detail ? `Échec de la génération : ${detail}` : 'Échec de la génération (problème réseau ou IA temporaire). Réessayez.');
      setStage('form');
    }
  };

  /** Upload direct "telle quelle" (0 crédit, aucune IA) — la photo devient l'image de
   * l'exercice sans passer par la génération. */
  const startCreateAsIs = async () => {
    if (!form.name.trim() || !form.muscle_group) { setCreateErr('Nom et groupe musculaire requis.'); return; }
    if (!refImage) return;
    setStage('generating'); setCreateErr('');
    try {
      let exId;
      const createRes = await fetchWithCsrf(`${BACKEND_URL}/api/exercises`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name_fr: form.name.trim(), muscle_group: form.muscle_group, equipment: form.equipment, level: form.level }),
      });
      if (createRes.ok) {
        exId = (await createRes.json()).id;
      } else if (createRes.status === 409) {
        const existing = await findExistingByName(form.name.trim());
        if (!existing) throw createRes;
        exId = existing.id;
      } else {
        throw createRes;
      }

      const uploaded = await fetchWithCsrf(`${BACKEND_URL}/api/exercises/${exId}/upload-image`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_base64: refImage.base64, content_type: refImage.contentType }),
      }).then((r) => (r.ok ? r.json() : Promise.reject(r)));

      setCreatedEx(uploaded);
      setShowVideoBlock(true);
      setStage('ready');
    } catch (err) {
      const detail = await parseErrDetail(err);
      setCreateErr(detail ? `Échec de l'upload : ${detail}` : "Échec de l'upload (problème réseau). Réessayez.");
      setStage('form');
    }
  };


  const openRegenExisting = (ex) => {
    setCreatedEx(ex);
    setStage('ready');
    setCreateOpen(true);
    setVideoStage('idle');
    setShowVideoBlock(false);
    setRegenCandidate(null); setRegenErr(''); setPendingRevertIdx(null); setRegenFeedback('');
    setRegenRefImage(null); setRegenRefImageErr('');
  };

  const runRegen = async (style) => {
    if (!createdEx) return;
    setRegenBusy(true); setRegenErr(''); setRegenCandidate(null);
    try {
      let ex = createdEx;
      if (!ex.execution || !ex.execution.trim()) {
        const text = await fetchWithCsrf(`${BACKEND_URL}/api/exercises/${ex.id}/generate-execution-text`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
        }).then((r) => (r.ok ? r.json() : Promise.reject(r)));
        await fetchWithCsrf(`${BACKEND_URL}/api/exercises/${ex.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ execution: text.execution, tips: text.tips }),
        });
        ex = { ...ex, execution: text.execution, tips: text.tips };
        setCreatedEx(ex);
      }
      const img = await fetchWithCsrf(
        regenRefImage ? `${BACKEND_URL}/api/exercises/${ex.id}/generate-image-candidate-from-upload` : `${BACKEND_URL}/api/exercises/${ex.id}/generate-image-candidate`,
        {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(regenRefImage
            ? { style, feedback: regenFeedback.trim(), image_base64: regenRefImage.base64, content_type: regenRefImage.contentType }
            : { style, feedback: regenFeedback.trim() }),
        },
      ).then((r) => (r.ok ? r.json() : Promise.reject(r)));
      setRegenCandidate(img);
    } catch (err) {
      const detail = await parseErrDetail(err);
      setRegenErr(detail ? `Échec : ${detail}` : 'Échec de la génération. Réessayez.');
    } finally { setRegenBusy(false); }
  };

  /** Upload direct "telle quelle" (0 IA) pour un exercice existant — remplace immédiatement
   * l'image active (avec rotation d'historique, comme /confirm-image). */
  const runRegenAsIs = async () => {
    if (!createdEx || !regenRefImage) return;
    setRegenBusy(true); setRegenErr('');
    try {
      const updated = await fetchWithCsrf(`${BACKEND_URL}/api/exercises/${createdEx.id}/upload-image`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_base64: regenRefImage.base64, content_type: regenRefImage.contentType }),
      }).then((r) => (r.ok ? r.json() : Promise.reject(r)));
      setCreatedEx(updated);
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
      setRegenRefImage(null);
      toast.success('Image mise à jour (upload direct).');
    } catch { toast.error("Échec de l'upload."); }
    finally { setRegenBusy(false); }
  };

  const confirmRegen = async () => {
    if (!regenCandidate || !createdEx) return;
    setRegenBusy(true);
    try {
      await fetchWithCsrf(`${BACKEND_URL}/api/exercises/${createdEx.id}/confirm-image`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: regenCandidate.image_url, image_path: regenCandidate.image_path, style: regenCandidate.style }),
      });
      const refreshed = await fetch(`${BACKEND_URL}/api/exercises/${createdEx.id}`, { credentials: 'include' }).then((r) => r.json());
      setCreatedEx(refreshed);
      setItems((prev) => prev.map((it) => (it.id === refreshed.id ? refreshed : it)));
      setRegenCandidate(null);
      toast.success('Nouvelle image enregistrée.');
    } catch { toast.error("Échec de l'enregistrement de l'image."); }
    finally { setRegenBusy(false); }
  };

  const revertRegenImage = async (idx) => {
    if (!createdEx) return;
    setRegenBusy(true);
    try {
      const updated = await fetchWithCsrf(`${BACKEND_URL}/api/exercises/${createdEx.id}/revert-image`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ history_index: idx }),
      }).then((r) => (r.ok ? r.json() : Promise.reject(r)));
      setCreatedEx(updated);
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
      toast.success('Image restaurée.');
    } catch { toast.error('Échec de la restauration.'); }
    finally { setRegenBusy(false); }
  };

  const startVideo = async () => {
    if (!createdEx) return;
    setVideoStage('generating');
    try {
      await fetchWithCsrf(`${BACKEND_URL}/api/exercises/${createdEx.id}/regenerate-video`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const poll = async () => {
        const st = await fetch(`${BACKEND_URL}/api/exercises/${createdEx.id}/video-status`, { credentials: 'include' }).then((r) => r.json());
        if (st.video_status === 'ready') {
          setCreatedEx((c) => ({ ...c, video_url: st.video_url }));
          setVideoStage('ready');
        } else if (st.video_status === 'failed') {
          setVideoStage('failed');
        } else {
          setTimeout(poll, 5000);
        }
      };
      setTimeout(poll, 5000);
    } catch {
      setVideoStage('failed');
    }
  };

  const useCreatedExercise = () => {
    if (!createdEx) return;
    onSelect(createdEx);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="exercise-picker-modal">
      <div className="bg-zinc-900 border border-violet-500/30 rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            {createOpen && (
              <button onClick={() => { setCreateOpen(false); setRegenCandidate(null); setRegenErr(''); }} className="text-zinc-500 hover:text-white mr-1" data-testid="exercise-create-back-btn">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <Dumbbell className="w-5 h-5 text-violet-400" /> {createOpen ? (stage === 'ready' ? "Image de l'exercice" : 'Nouvel exercice') : "Bibliothèque d'exercices"}
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors" data-testid="exercise-picker-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {createOpen ? (
          <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="exercise-create-panel">
            {stage === 'form' && (
              <>
                <div className="space-y-3">
                  <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} onBlur={onNameBlur}
                    placeholder="Nom de l'exercice" autoFocus
                    className="w-full bg-zinc-800 border border-white/10 rounded-xl px-3.5 py-3 text-white text-sm focus:border-violet-500/50 focus:outline-none"
                    data-testid="exercise-create-name-input" />
                  <div className="grid grid-cols-2 gap-2.5">
                    <select value={form.muscle_group} onChange={(e) => setForm((f) => ({ ...f, muscle_group: e.target.value }))}
                      className="bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-xs focus:border-violet-500/50 focus:outline-none"
                      data-testid="exercise-create-muscle-select">
                      <option value="">Groupe musculaire...</option>
                      {muscleGroups.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <select value={form.level} onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}
                      className="bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-xs focus:border-violet-500/50 focus:outline-none"
                      data-testid="exercise-create-level-select">
                      {levels.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                    </select>
                  </div>
                  <select value={form.equipment} onChange={(e) => setForm((f) => ({ ...f, equipment: e.target.value }))}
                    className="w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-xs focus:border-violet-500/50 focus:outline-none"
                    data-testid="exercise-create-equipment-select">
                    {equipments.map((eq) => <option key={eq.key} value={eq.key}>{eq.label}</option>)}
                  </select>
                </div>

                {createErr && <p className="text-xs text-red-400" data-testid="exercise-create-error">{createErr}</p>}

                <div>
                  <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-2">Instructions pour l&apos;image (optionnel)</p>
                  <textarea value={createFeedback} onChange={(e) => setCreateFeedback(e.target.value)}
                    placeholder="Ex: vue de face pour bien voir le ballon entre les 2 genoux, dos bien droit, prise en pronation..."
                    className="w-full bg-zinc-800 border border-white/10 rounded-xl p-2.5 text-xs text-white placeholder:text-zinc-500 focus:border-violet-500/50 focus:outline-none"
                    rows={2} data-testid="exercise-create-feedback-textarea" />
                </div>

                <div>
                  <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-2">Image de référence (optionnel)</p>
                  {!refImage ? (
                    <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 rounded-xl py-6 cursor-pointer hover:border-violet-500/40 transition-colors" data-testid="exercise-create-upload-dropzone">
                      <UploadCloud className="w-6 h-6 text-zinc-500" />
                      <span className="text-[11px] text-zinc-500 text-center px-3">Uploadez une photo trouvée sur internet — JPG, PNG ou WebP, 5 Mo max</span>
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; setRefImageErr(''); readUploadFile(f).then(setRefImage).catch(setRefImageErr); }}
                        data-testid="exercise-create-upload-input" />
                    </label>
                  ) : (
                    <div className="flex items-center gap-3 bg-zinc-800/60 border border-white/10 rounded-xl p-2.5" data-testid="exercise-create-upload-preview">
                      <img src={refImage.previewUrl} alt="référence" className="w-14 h-14 rounded-lg object-cover border border-white/10" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-zinc-300">Image chargée — choisissez ci-dessous.</p>
                      </div>
                      <button onClick={() => setRefImage(null)} className="text-zinc-500 hover:text-white shrink-0" data-testid="exercise-create-upload-remove-btn">
                        <ImageOff className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {refImageErr && <p className="text-xs text-red-400 mt-1.5" data-testid="exercise-create-upload-error">{refImageErr}</p>}
                  {refImage && (
                    <button onClick={startCreateAsIs}
                      className="w-full mt-2.5 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
                      data-testid="exercise-create-as-is-btn">
                      <CheckCircle2 className="w-4 h-4" /> Garder cette image telle quelle (0 crédit)
                    </button>
                  )}
                </div>

                <div>
                  <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-2">
                    Style visuel {refImage ? '— IA avec votre image comme référence' : '(IA, texte seul)'}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {STYLE_CHOICES.map(({ key, label, Icon, testid }) => (
                      <button key={key} onClick={() => startCreate(key)}
                        className="flex flex-col items-center gap-2 py-5 rounded-2xl border border-white/10 bg-zinc-800/60 hover:border-violet-500/50 hover:bg-zinc-800 transition-colors"
                        data-testid={testid}>
                        <Icon className="w-6 h-6 text-violet-400" />
                        <span className="text-xs font-bold text-white text-center px-2">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {stage === 'generating' && (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-center" data-testid="exercise-create-generating">
                <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
                <p className="text-sm text-zinc-400">Génération du texte technique et de l&apos;image en cours...</p>
              </div>
            )}

            {stage === 'ready' && createdEx && (
              <div className="space-y-4" data-testid="exercise-create-ready">
                <div className="w-40 aspect-square mx-auto rounded-xl overflow-hidden bg-zinc-950 border border-white/10">
                  <ExerciseImage src={createdEx.image_url} alt={createdEx.name_fr} />
                </div>
                <p className="text-center text-white font-bold">{createdEx.name_fr}</p>

                <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-3.5 space-y-3" data-testid="regen-image-section">
                  <p className="text-xs font-bold text-zinc-300 flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5 text-violet-400" /> Pas convaincu par le rendu ? Expliquez ce qu&apos;il faut changer et régénérez :
                  </p>
                  <div>
                    {!regenRefImage ? (
                      <label className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-white/15 rounded-lg py-4 cursor-pointer hover:border-violet-500/40 transition-colors" data-testid="regen-upload-dropzone">
                        <UploadCloud className="w-5 h-5 text-zinc-500" />
                        <span className="text-[10px] text-zinc-500 text-center px-3">Ou uploadez une photo de référence — JPG, PNG ou WebP, 5 Mo max</span>
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; setRegenRefImageErr(''); readUploadFile(f).then(setRegenRefImage).catch(setRegenRefImageErr); }}
                          data-testid="regen-upload-input" />
                      </label>
                    ) : (
                      <div className="flex items-center gap-2.5 bg-zinc-900 border border-white/10 rounded-lg p-2" data-testid="regen-upload-preview">
                        <img src={regenRefImage.previewUrl} alt="référence" className="w-10 h-10 rounded-md object-cover border border-white/10" />
                        <p className="flex-1 text-[10px] text-zinc-300">Image chargée.</p>
                        <button onClick={() => setRegenRefImage(null)} className="text-zinc-500 hover:text-white shrink-0" data-testid="regen-upload-remove-btn">
                          <ImageOff className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    {regenRefImageErr && <p className="text-[11px] text-red-400 mt-1" data-testid="regen-upload-error">{regenRefImageErr}</p>}
                    {regenRefImage && (
                      <button onClick={runRegenAsIs} disabled={regenBusy}
                        className="w-full mt-2 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition-colors"
                        data-testid="regen-as-is-btn">
                        {regenBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Garder cette image telle quelle (0 crédit)
                      </button>
                    )}
                  </div>
                  <textarea value={regenFeedback} onChange={(e) => setRegenFeedback(e.target.value)}
                    placeholder="Ex: vue de face pour bien voir le ballon entre les 2 genoux, dos bien droit, prise en pronation, coudes plus serrés..."
                    className="w-full bg-zinc-900 border border-white/10 rounded-lg p-2.5 text-xs text-white placeholder:text-zinc-500 focus:border-violet-500/50 focus:outline-none"
                    rows={2} data-testid="regen-feedback-textarea" />
                  {regenErr && <p className="text-xs text-red-400" data-testid="regen-image-error">{regenErr}</p>}
                  <p className="text-[10px] text-zinc-500">{regenRefImage ? 'Régénérer avec l\'IA en utilisant votre image comme référence :' : 'Régénérer avec l\'IA (texte seul) :'}</p>
                  <div className="grid grid-cols-2 gap-2.5">
                    {STYLE_CHOICES.map(({ key, label, Icon, testid }) => (
                      <button key={key} onClick={() => runRegen(key)} disabled={regenBusy}
                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-white/10 bg-zinc-800/60 hover:border-violet-500/50 hover:bg-zinc-800 transition-colors disabled:opacity-50"
                        data-testid={`regen-${testid}`}>
                        {regenBusy ? <Loader2 className="w-5 h-5 text-violet-400 animate-spin" /> : <Icon className="w-5 h-5 text-violet-400" />}
                        <span className="text-[11px] font-bold text-white text-center px-1">{label}</span>
                      </button>
                    ))}
                  </div>
                  {regenCandidate && (
                    <div className="space-y-2.5" data-testid="regen-candidate-panel">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <p className="text-[10px] font-bold text-zinc-500 uppercase text-center">Actuelle</p>
                          <div className="w-full aspect-square rounded-lg overflow-hidden bg-zinc-950 border border-white/10">
                            <ExerciseImage src={createdEx.image_url} alt="avant" />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <p className="text-[10px] font-bold text-violet-300 uppercase text-center">Nouveau candidat</p>
                          <div className="w-full aspect-square rounded-lg overflow-hidden bg-zinc-950 border border-violet-500/40" data-testid="regen-candidate-image">
                            <ExerciseImage src={regenCandidate.image_url} alt="candidat" />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={confirmRegen} disabled={regenBusy}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition-colors"
                          data-testid="regen-confirm-btn">
                          {regenBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Utiliser ce candidat
                        </button>
                        <button onClick={() => setRegenCandidate(null)} disabled={regenBusy}
                          className="px-3 py-2 rounded-lg text-xs font-bold text-zinc-400 hover:text-white transition-colors"
                          data-testid="regen-discard-btn">
                          Ignorer
                        </button>
                      </div>
                    </div>
                  )}
                  {createdEx.image_history && createdEx.image_history.length > 0 && (
                    <div className="space-y-1.5 pt-1" data-testid="regen-image-history">
                      <p className="text-[10px] font-bold text-zinc-500 uppercase">Versions précédentes</p>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        {createdEx.image_history.map((h, i) => (
                          <div key={i} className="flex flex-col items-center gap-1" data-testid={`regen-history-item-${i}`}>
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-zinc-950 border border-white/10">
                              <ExerciseImage src={h.image_url} alt="version précédente" />
                            </div>
                            <button onClick={() => setPendingRevertIdx(i)} disabled={regenBusy}
                              className="text-[9px] font-bold text-violet-300 hover:text-white disabled:opacity-50"
                              data-testid={`regen-history-revert-${i}`}>
                              Restaurer
                            </button>
                          </div>
                        ))}
                      </div>
                      {pendingRevertIdx != null && createdEx.image_history[pendingRevertIdx] && (
                        <div className="space-y-2 pt-1" data-testid="regen-revert-compare-panel">
                          <p className="text-[10px] font-bold text-amber-300 uppercase text-center">Confirmer la restauration ?</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <p className="text-[10px] font-bold text-zinc-500 uppercase text-center">Actuelle</p>
                              <div className="w-full aspect-square rounded-lg overflow-hidden bg-zinc-950 border border-white/10">
                                <ExerciseImage src={createdEx.image_url} alt="actuelle" />
                              </div>
                            </div>
                            <div className="space-y-1">
                              <p className="text-[10px] font-bold text-amber-300 uppercase text-center">Version à restaurer</p>
                              <div className="w-full aspect-square rounded-lg overflow-hidden bg-zinc-950 border border-amber-500/40" data-testid="regen-revert-preview-image">
                                <ExerciseImage src={createdEx.image_history[pendingRevertIdx].image_url} alt="à restaurer" />
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => { revertRegenImage(pendingRevertIdx); setPendingRevertIdx(null); }} disabled={regenBusy}
                              className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-bold text-white bg-amber-600 hover:bg-amber-500 disabled:opacity-50 transition-colors"
                              data-testid="regen-revert-confirm-btn">
                              {regenBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirmer la restauration
                            </button>
                            <button onClick={() => setPendingRevertIdx(null)} disabled={regenBusy}
                              className="px-3 py-2 rounded-lg text-xs font-bold text-zinc-400 hover:text-white transition-colors"
                              data-testid="regen-revert-cancel-btn">
                              Annuler
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {showVideoBlock && (
                <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-3.5">
                  {videoStage === 'idle' && (
                    <button onClick={startVideo}
                      className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold text-amber-300 hover:text-white transition-colors"
                      data-testid="create-generate-video-btn">
                      <Film className="w-4 h-4" /> Générer une vidéo Sora 2 (optionnel, ~2-5 min)
                    </button>
                  )}
                  {videoStage === 'generating' && (
                    <p className="text-xs text-amber-300 flex items-center justify-center gap-2 text-center" data-testid="create-video-generating">
                      <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" /> Vidéo en cours de génération (2-5 min) — vous pouvez déjà utiliser l&apos;exercice, elle s&apos;ajoutera automatiquement.
                    </p>
                  )}
                  {videoStage === 'ready' && (
                    <p className="text-xs text-emerald-400 flex items-center justify-center gap-2" data-testid="create-video-ready">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Vidéo générée avec succès !
                    </p>
                  )}
                  {videoStage === 'failed' && (
                    <p className="text-xs text-red-400 text-center" data-testid="create-video-failed">
                      Échec de la génération vidéo — réessayable depuis la Bibliothèque d&apos;exercices.
                    </p>
                  )}
                </div>
                )}

                <button onClick={useCreatedExercise}
                  className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:scale-[1.01] transition-transform"
                  data-testid="create-use-exercise-btn">
                  <Sparkles className="w-4 h-4" /> Utiliser cet exercice
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="p-4 pb-2 space-y-3 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Rechercher un exercice..."
                  className="w-full bg-zinc-800 border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-white text-sm focus:border-violet-500/50 focus:outline-none"
                  data-testid="exercise-picker-search-input" />
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="exercise-picker-muscle-filters">
                <button onClick={() => setActiveGroup('')}
                  className={`px-3 py-2 rounded-2xl text-[11px] font-bold whitespace-nowrap border shrink-0 ${!activeGroup ? 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white border-transparent' : 'bg-zinc-800/60 text-zinc-300 border-white/10'}`}
                  data-testid="exercise-picker-filter-all">Tous</button>
                {muscleGroups.map((g) => (
                  <MuscleGroupChip key={g} group={g} active={activeGroup === g} onClick={() => setActiveGroup(activeGroup === g ? '' : g)} testid={`exercise-picker-filter-${g}`} />
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 pt-1">
              {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-violet-400 animate-spin" /></div>
              ) : items.length === 0 ? (
                <div className="text-center py-8 space-y-3" data-testid="exercise-picker-no-results">
                  <p className="text-sm text-zinc-500">Aucun exercice trouvé{q.trim() ? ` pour "${q.trim()}"` : ''}.</p>
                  {q.trim() && (
                    <button onClick={openCreate}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600/20 text-violet-300 rounded-xl text-sm font-bold hover:bg-violet-600/30 transition-colors"
                      data-testid="exercise-picker-create-new-btn">
                      <Sparkles className="w-4 h-4" /> Créer &quot;{q.trim()}&quot;
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="exercise-picker-grid">
                    {items.slice(0, 90).map((ex) => (
                      <div key={ex.id} className="relative">
                        <button onClick={() => onSelect(ex)}
                          className="text-left w-full bg-zinc-800/60 border border-white/10 rounded-xl overflow-hidden hover:border-violet-500/50 hover:scale-[1.02] transition-all"
                          data-testid={`exercise-picker-card-${ex.id}`}>
                          <div className="w-full aspect-square bg-zinc-950">
                            <ExerciseImage src={ex.image_url} alt={ex.name_fr} />
                          </div>
                          <div className="p-2">
                            <p className="text-xs font-semibold text-white line-clamp-2">{ex.name_fr}</p>
                            <p className="text-[10px] text-zinc-500 mt-0.5">{ex.muscle_group}</p>
                          </div>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); openRegenExisting(ex); }}
                          title="Régénérer l'image de cet exercice"
                          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 border border-white/20 flex items-center justify-center text-violet-300 hover:text-white hover:bg-black/90 transition-colors"
                          data-testid={`exercise-picker-regen-${ex.id}`}>
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {q.trim() && (
                    <button onClick={openCreate} className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-violet-400 hover:text-white" data-testid="exercise-picker-create-new-link">
                      <Sparkles className="w-3.5 h-3.5" /> Aucun ne convient ? Créer &quot;{q.trim()}&quot;
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExercisePickerModal;
